import { useCallback, useEffect, useRef } from 'react'
import toast from 'react-hot-toast'

import {
  resetComponentsSettings,
  resetSettingsLoadedAction,
  setComponentsSettingsLoading,
} from 'src/redux/actions/componentsSettingsActions'
import { useAppSelector } from 'src/redux/hooks'
import useDashboardsService from 'src/services/dashboardService'
import useFilterBarStore from 'src/stores/useFilterBarStore'
import { getDashboardKeyFn } from 'src/utils/saveDashboardUtils'
import { ComponentsSettings } from 'src/utils/settings'
import { waitForDelayFn } from 'src/utils/waitForDelayUtils'
import useDispatcher from './useDispatcher'
import useHasChangeOnPage from './useHasChangeOnPage'
// import useFilterBar from './useFilterBar'
import useSettings from './useSettings'

const useLoadDashboard = (subProductCode: string) => {
  const { getDashboard } = useDashboardsService()
  const resetSettingsLoadedDispatcher = useDispatcher(resetSettingsLoadedAction)
  const setComponentsSettingsLoadingDispatcher = useDispatcher(setComponentsSettingsLoading)
  const resetComponentsSettingsDispatcher = useDispatcher(resetComponentsSettings)
  const { setHasChangesOnDashboard } = useHasChangeOnPage()
  const { setDataWarnings, setFiltersShapes } = useFilterBarStore()

  const { clearQueryParams } = useSettings()
  const settings = useAppSelector(s => s.componentsSettings.settings)
  const oldSettings = useAppSelector(s => s.componentsSettings.oldSettings)

  const settingsRef = useRef(settings)
  const oldSettingsRef = useRef(oldSettings)

  useEffect(() => {
    settingsRef.current = settings
    oldSettingsRef.current = oldSettings
  }, [settings, oldSettings])

  const handleGetDashboard = useCallback(
    async ({ id, programId }: { id: string | null; programId?: string }) => {
      setComponentsSettingsLoadingDispatcher(true)
      resetSettingsLoadedDispatcher()
      clearQueryParams({ from: '' })
      setFiltersShapes(null)
      setDataWarnings({})
      let settings: ComponentsSettings
      let oldSettings: ComponentsSettings
      const dashboardKey = getDashboardKeyFn({
        subProductCode,
        programId: programId?.toString(),
      })

      if (id) {
        const { data, error }: { data: ComponentsSettings | null; error: any } =
          (await getDashboard({ id, programId, subProductCode }).catch(error => {
            return { data: null, error }
          })) as any
        if (!data) {
          toast.error('An error occurred. Please retry.')
          setComponentsSettingsLoadingDispatcher(false)
          throw error
        }
        const { components_settings } = data
        settings = {
          ...settingsRef.current,
          [dashboardKey]: components_settings[subProductCode],
        }
        oldSettings = {
          ...oldSettingsRef.current,
          [dashboardKey]: components_settings[subProductCode],
        }
      } else {
        settings = { ...settingsRef.current, [dashboardKey]: {} }
        oldSettings = { ...oldSettingsRef.current, [dashboardKey]: {} }
      }

      resetComponentsSettingsDispatcher(settings, oldSettings)
      setHasChangesOnDashboard(false)
      await waitForDelayFn(500)
      setComponentsSettingsLoadingDispatcher(false)
    },
    [
      getDashboard,
      setComponentsSettingsLoadingDispatcher,
      resetSettingsLoadedDispatcher,
      clearQueryParams,
      setFiltersShapes,
      setDataWarnings,
      setHasChangesOnDashboard,
      resetComponentsSettingsDispatcher,
      subProductCode,
    ],
  )

  return {
    handleGetDashboard,
  }
}

export default useLoadDashboard
